/**
 * Bulk Data Operations Page
 *
 * Full page for running bulk operations (retag exceptions, reclassify invoices)
 * across franchise partners. Uses same FP list pattern as Franchise Partners page.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Chip,
  Button,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Collapse,
  Tooltip,
} from '@mui/material'
import {
  PlayArrow as RunIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material'
import { apiClient } from '../../../services/api'
import AdminPageHeader from '../../../components/admin/shared/AdminPageHeader'
import { useFranchisePartnersData } from '../../../components/admin/franchise-partners/hooks/useFranchisePartnersData'
import { useFranchisePartnersFilters } from '../../../components/admin/franchise-partners/hooks/useFranchisePartnersFilters'
import { useBulkOperations } from '../../../components/admin/franchise-partners/hooks/useBulkOperations'
import FilterCard from '../../../components/admin/franchise-partners/filters/FilterCard'
import PartnerSelectionTable, { PartnerExtraColumn } from '../../../components/admin/franchise-partners/PartnerSelectionTable'

type PageState = 'select' | 'processing' | 'complete' | 'error'
type Operation = 'retag_exceptions' | 'reclassify_invoices' | 'reclassify_outlet_register_types' | 'resync_ls_sale_dates' | 'resync_ls_sale_notes' | 'resync_consignment_data' | 'resync_ls_product_data' | 'backfill_sales' | 'backfill_sale_date_local' | 'backfill_last_received_date_local' | 'backfill_sk_sync_errors' | 'backfill_manual_journal_authors'
type Category = 'Exceptions' | 'Classification' | 'Lightspeed (API)' | 'Lightspeed (DB only)' | 'Lightspeed (Sync)' | 'Xero (API)' | 'Skimmer'
type CostClass = 'api' | 'sql' | 'queue'
type ResultSection = 'succeeded' | 'failed' | 'skipped'

interface OperationConfig {
  key: Operation
  label: string
  category: Category
  costClass: CostClass
  secondary: string
  summary: string
  bullets: string[]
}

const CATEGORIES: Category[] = ['Exceptions', 'Classification', 'Lightspeed (API)', 'Lightspeed (DB only)', 'Lightspeed (Sync)', 'Xero (API)', 'Skimmer']

const COST_LABEL: Record<CostClass, string> = {
  api: 'API',
  sql: 'DB',
  queue: 'SYNC',
}

const COST_COLOR: Record<CostClass, 'warning' | 'default' | 'secondary'> = {
  api: 'warning',
  sql: 'default',
  queue: 'secondary',
}

const OPERATIONS: OperationConfig[] = [
  {
    key: 'retag_exceptions',
    label: 'Retag Exceptions',
    category: 'Exceptions',
    costClass: 'sql',
    secondary: 'Re-runs exception tagging with the latest rules',
    summary: 'Clears and re-runs exception tagging for each selected FP using the latest rules.',
    bullets: [
      'Removes all existing exception tags',
      'Re-applies tags using the current exception type definitions',
      'Runs automatically after every other bulk operation',
    ],
  },
  {
    key: 'reclassify_invoices',
    label: 'Reclassify Xero Invoices',
    category: 'Classification',
    costClass: 'sql',
    secondary: 'Re-detects source system and origin type',
    summary: 'Clears Xero invoice classifications and re-runs source detection.',
    bullets: [
      'Detected source system (Lightspeed, Pooltrackr, Skimmer, manual)',
      'Detected origin type (sale, consignment, manual entry, etc.)',
      'Linked Lightspeed consignment (for invoices tied to stock transfers)',
    ],
  },
  {
    key: 'reclassify_outlet_register_types',
    label: 'Reclassify LS Outlet / Register Types',
    category: 'Classification',
    costClass: 'sql',
    secondary: 'Re-classifies outlets and registers from names + sales',
    summary: 'Re-classifies Lightspeed outlets and registers from name patterns and sales data.',
    bullets: [
      'Outlet type (retail, mobile, warehouse, etc.)',
      'Register type (retail, Pooltrackr, Skimmer, etc.)',
      'Falls back to sales-source distribution when names are ambiguous',
    ],
  },
  {
    key: 'resync_ls_sale_dates',
    label: 'Resync LS Sale Dates',
    category: 'Lightspeed (API)',
    costClass: 'api',
    secondary: 'Pulls correct sale dates from Lightspeed',
    summary: 'Pulls correct sale dates from Lightspeed and fixes any that drifted.',
    bullets: [
      'Sale date (UTC, from Lightspeed)',
      'Local sale date (converted to the FP\u2019s timezone)',
      'Last-sold date on affected products',
    ],
  },
  {
    key: 'resync_ls_sale_notes',
    label: 'Resync LS Sale Notes',
    category: 'Lightspeed (API)',
    costClass: 'api',
    secondary: 'Backfills sale-level notes from Lightspeed',
    summary: 'Backfills the sale-level note field from Lightspeed (Pooltrackr service annotations).',
    bullets: [
      'Sale note text (Date of service, Pool address, Service, PO number, Technician)',
      'Used by the duplicate-sale detector to veto false positives on repeated service packages',
      'Full sale refetch; does not advance the incremental sync cursor',
    ],
  },
  {
    key: 'resync_consignment_data',
    label: 'Resync LS Consignment Data',
    category: 'Lightspeed (API)',
    costClass: 'api',
    secondary: 'Refetches consignment dates and costs from Lightspeed',
    summary: 'Refetches consignment data from Lightspeed to refresh dates and costs.',
    bullets: [
      'Due date (UTC and local)',
      'Received-at local date',
      'Total cost (pulled from the Lightspeed totals endpoint)',
    ],
  },
  {
    key: 'resync_ls_product_data',
    label: 'Resync LS Product Data',
    category: 'Lightspeed (API)',
    costClass: 'api',
    secondary: 'Refetches all products from Lightspeed',
    summary: 'Refetches all products from Lightspeed to backfill fields the original sync missed.',
    bullets: [
      'Product category links (powers the Product Catalogue Review tool)',
      'Runs a full product refetch without touching the incremental sync cursor',
    ],
  },
  {
    key: 'backfill_sale_date_local',
    label: 'Backfill LS Sale Local Dates',
    category: 'Lightspeed (DB only)',
    costClass: 'sql',
    secondary: 'Recalc sale dates in the FP timezone',
    summary: 'Recalculates each sale’s local date (the date in the FP’s own timezone) from the existing UTC value, then refreshes each product’s last-sold date from those updated sales.',
    bullets: [
      'Recomputes the local date on every sale using the existing UTC date',
      'Refreshes each product’s last-sold date from the updated sales',
      'Use after an FP timezone change, or to retry FPs missed in a previous backfill',
      'Pick “Resync LS Sale Dates” instead if the UTC date itself is wrong - that one re-fetches from Lightspeed (slower)',
    ],
  },
  {
    key: 'backfill_last_received_date_local',
    label: 'Backfill LS Product Last-Received Dates',
    category: 'Lightspeed (DB only)',
    costClass: 'sql',
    secondary: 'Recalc product receive dates in the FP timezone',
    summary: 'Recalculates each product’s last-received date in the FP’s own timezone using the existing UTC value.',
    bullets: [
      'Only updates products that have a received date but no local equivalent yet',
      'Use after an FP timezone change, or to retry FPs missed in a previous backfill',
    ],
  },
  {
    key: 'backfill_sales',
    label: 'Backfill LS Sales',
    category: 'Lightspeed (Sync)',
    costClass: 'queue',
    secondary: 'Queues catch-up syncs to recover missing sales',
    summary: 'Queues a Lightspeed sync for each selected FP to fill in missing sales since their last successful sync.',
    bullets: [
      'Each FP picks up where its last successful sync left off',
      'Syncs run in the background - check the Sync Dashboard for progress',
      'Skips any FP already mid-sync',
      'Bad rows are skipped and logged rather than failing the whole sync',
      'Best for fixing sales gaps after a sync was stuck or failing',
    ],
  },
  {
    key: 'backfill_manual_journal_authors',
    label: 'Backfill Manual Journal Authors',
    category: 'Xero (API)',
    costClass: 'api',
    secondary: 'Captures who created / last edited each manual journal',
    summary: 'Fetches the Xero History for each POSTED manual journal to record who created and last edited it (the ManualJournals endpoint carries no user).',
    bullets: [
      'Populates Posted By / Posted Date / Last Edited By / Last Edited Date',
      'Only fetches journals not captured yet - safe to re-run / resume',
      'One Xero History call per journal; slow for FPs with many journals',
      'Surfaces on the "Manual journal on COGS base" exception',
      '"System Generated" means the journal was posted via the Xero API, not a person',
    ],
  },
  {
    key: 'backfill_sk_sync_errors',
    label: 'Backfill SK Sync Errors (14d)',
    category: 'Skimmer',
    costClass: 'api',
    secondary: 'Re-ingest last 14 days of Skimmer error CSVs (US only)',
    summary: 'Re-ingests the last 14 days of Skimmer sync-error CSVs and re-tags exceptions for each selected FP.',
    bullets: [
      'Re-processes every CSV in the last 14 days regardless of watermark',
      'Reopens any errors that re-appeared after being marked resolved',
      'Re-runs the two Skimmer-Sync taggers (sales + client) if anything changed',
      'US franchises only',
    ],
  },
]

interface JobStatus {
  job_id: string
  status: string
  progress_percentage: number
  progress_message: string | null
  result_summary: {
    operation: string
    scope: string
    franchises_processed: number
    franchises_succeeded: number
    franchises_failed: number
    franchises_skipped?: number
    total_affected: number
    duration_seconds: number
    succeeded_fp_codes?: string[]
    succeeded_fp_codes_dropped?: number
    failed_fps?: Array<{ fp_code: string; error: string }>
    failed_fps_dropped?: number
    skipped_fp_codes?: string[]
    skipped_fp_codes_dropped?: number
  } | null
  error_message: string | null
  created_at: string | null
  completed_at: string | null
}

const formatCount = (count?: number): string => {
  if (count === undefined || count === null) return '\u2014'
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`
  return count.toString()
}

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return `${mins}m ${secs}s`
}

type ResultSummary = NonNullable<JobStatus['result_summary']>

interface SummaryTileProps {
  label: string
  value: React.ReactNode
  section?: ResultSection
  active?: boolean
  valueColor?: string
  onClick?: () => void
}

const SummaryTile: React.FC<SummaryTileProps> = ({ label, value, section, active, valueColor, onClick }) => {
  const clickable = section !== undefined
  return (
    <Box
      onClick={clickable ? onClick : undefined}
      sx={{
        cursor: clickable ? 'pointer' : 'default',
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        border: 1,
        borderColor: active ? 'primary.main' : 'divider',
        bgcolor: active ? 'action.selected' : 'background.paper',
        '&:hover': clickable ? { bgcolor: 'action.hover' } : undefined,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 110,
      }}
    >
      <Typography variant="caption" color="textSecondary">{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="h6" sx={{ color: valueColor, lineHeight: 1.2 }}>{value}</Typography>
        {clickable && (active
          ? <ExpandLessIcon fontSize="small" color="action" />
          : <ExpandMoreIcon fontSize="small" color="action" />)}
      </Box>
    </Box>
  )
}

interface DetailsPanelProps {
  open: boolean
  title: string
  droppedCount?: number
  children: React.ReactNode
}

const DetailsPanel: React.FC<DetailsPanelProps> = ({ open, title, droppedCount, children }) => (
  <Collapse in={open} unmountOnExit>
    <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      {children}
      {!!droppedCount && droppedCount > 0 && (
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          +{droppedCount} more not listed
        </Typography>
      )}
    </Box>
  </Collapse>
)

interface ResultsCardProps {
  summary: ResultSummary
  expandedSection: ResultSection | null
  onToggleSection: (section: ResultSection) => void
  onReset: () => void
}

const ResultsCard: React.FC<ResultsCardProps> = ({ summary, expandedSection, onToggleSection, onReset }) => {
  const failed = summary.franchises_failed
  const succeeded = summary.franchises_succeeded
  const skipped = summary.franchises_skipped ?? 0
  const succeededCodes = summary.succeeded_fp_codes ?? []
  const failedEntries = summary.failed_fps ?? []
  const skippedCodes = summary.skipped_fp_codes ?? []
  const opLabel =
    OPERATIONS.find((op) => op.key === summary.operation)?.label ||
    summary.operation

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Alert severity={failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
          {failed > 0
            ? `${opLabel} completed with failures`
            : `${opLabel} complete`}
        </Alert>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <SummaryTile label="Processed" value={summary.franchises_processed} />
          <SummaryTile
            label="Succeeded"
            value={succeeded}
            section="succeeded"
            active={expandedSection === 'succeeded'}
            valueColor="success.main"
            onClick={() => onToggleSection('succeeded')}
          />
          <SummaryTile
            label="Failed"
            value={failed}
            section="failed"
            active={expandedSection === 'failed'}
            valueColor={failed > 0 ? 'error.main' : undefined}
            onClick={() => onToggleSection('failed')}
          />
          {skipped > 0 && (
            <SummaryTile
              label="Skipped"
              value={skipped}
              section="skipped"
              active={expandedSection === 'skipped'}
              onClick={() => onToggleSection('skipped')}
            />
          )}
          <SummaryTile label="Duration" value={formatDuration(summary.duration_seconds)} />
        </Box>

        <DetailsPanel
          open={expandedSection === 'succeeded'}
          title={`Succeeded (${succeeded})`}
          droppedCount={summary.succeeded_fp_codes_dropped}
        >
          {succeededCodes.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {succeededCodes.map((code) => (
                <Chip
                  key={code}
                  label={code}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontFamily: 'monospace' }}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No FPs recorded</Typography>
          )}
        </DetailsPanel>

        <DetailsPanel
          open={expandedSection === 'failed'}
          title={`Failed (${failed})`}
          droppedCount={summary.failed_fps_dropped}
        >
          {failedEntries.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {failedEntries.map((entry, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Chip
                    label={entry.fp_code}
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace', flexShrink: 0 }}
                  />
                  <Typography variant="body2" color="error.main">{entry.error}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No details recorded</Typography>
          )}
        </DetailsPanel>

        <DetailsPanel
          open={expandedSection === 'skipped'}
          title={`Skipped (${skipped})`}
          droppedCount={summary.skipped_fp_codes_dropped}
        >
          {skippedCodes.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {skippedCodes.map((code) => (
                <Chip
                  key={code}
                  label={code}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: 'monospace' }}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No FPs recorded</Typography>
          )}
        </DetailsPanel>

        <Button variant="outlined" onClick={onReset}>
          Run Another Operation
        </Button>
      </CardContent>
    </Card>
  )
}

const bulkExtraColumns: PartnerExtraColumn[] = [
  {
    key: 'ls_data',
    header: 'LS Data',
    align: 'right',
    render: (fp) => formatCount(fp.lightspeed_record_count),
  },
  {
    key: 'xero_data',
    header: 'Xero Data',
    align: 'right',
    render: (fp) => formatCount(fp.xero_record_count),
  },
]

const BulkOperationsPage: React.FC = () => {
  const { franchisePartners, loading } = useFranchisePartnersData()
  const {
    filters,
    updateFilter,
    clearFilters,
    filteredData,
    paginatedData,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
  } = useFranchisePartnersFilters(franchisePartners)
  const {
    selectedFranchises,
    handleSelectFranchise,
    handleSelectAll,
    handleSelectAllFiltered,
    clearSelection,
    isSelected,
    isAllSelected,
    isAllFilteredSelected,
    isIndeterminate,
  } = useBulkOperations()

  const [pageState, setPageState] = useState<PageState>('select')
  const [operation, setOperation] = useState<Operation>('retag_exceptions')
  const [confirmed, setConfirmed] = useState(false)
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [expandedSection, setExpandedSection] = useState<ResultSection | null>(null)
  const pollingRef = useRef<number | null>(null)

  useEffect(() => {
    if (pageState !== 'complete' || !jobStatus?.result_summary) return
    setExpandedSection(jobStatus.result_summary.franchises_failed > 0 ? 'failed' : null)
  }, [pageState, jobStatus?.result_summary])

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      window.clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => stopPolling()
  }, [stopPolling])

  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const response = await apiClient.get(
        `/system/admin-tools/bulk-operations/jobs/${jobId}`
      )
      const status: JobStatus = response.data
      setJobStatus(status)

      if (status.status === 'completed') {
        setPageState('complete')
        stopPolling()
      } else if (status.status === 'failed') {
        setPageState('error')
        setErrorMessage(status.error_message || 'Operation failed')
        stopPolling()
      }
    } catch (err) {
      console.error('Failed to poll job status:', err)
    }
  }, [stopPolling])

  const handleRun = async () => {
    const scope = selectedFranchises.size === 0 ? 'all' : 'specific'
    const fpCodes = scope === 'specific'
      ? franchisePartners
          .filter((fp) => selectedFranchises.has(fp.fp_code))
          .sort((a, b) => a.franchise_name.localeCompare(b.franchise_name))
          .map((fp) => fp.fp_code)
      : undefined

    setPageState('processing')
    setErrorMessage(null)
    setJobStatus(null)

    try {
      const response = await apiClient.post(
        '/system/admin-tools/bulk-operations',
        {
          operation,
          scope,
          fp_codes: fpCodes,
        }
      )

      const jobId = response.data.job_id
      setJobStatus({
        job_id: jobId,
        status: 'pending',
        progress_percentage: 0,
        progress_message: 'Queued...',
        result_summary: null,
        error_message: null,
        created_at: null,
        completed_at: null,
      })

      // Start polling
      const poll = () => pollJobStatus(jobId)
      poll()
      pollingRef.current = window.setInterval(poll, 2000)
    } catch (err: any) {
      setPageState('error')
      setErrorMessage(
        err.response?.data?.detail || err.message || 'Failed to submit operation'
      )
    }
  }

  const handleReset = () => {
    setPageState('select')
    setJobStatus(null)
    setErrorMessage(null)
    setConfirmed(false)
    setExpandedSection(null)
    stopPolling()
  }

  const isProcessing = pageState === 'processing'
  const selectionCount = selectedFranchises.size
  const selectedOp = OPERATIONS.find((op) => op.key === operation)!
  const operationLabel = selectedOp.label

  return (
    <>
      <AdminPageHeader
        pageKey="bulkOperations"
        titleOverride="Bulk Data Operations"
        descriptionOverride="Select franchise partners and run bulk operations"
        statusChip={
          <Chip label="Internal Use Only" size="small" color="warning" variant="outlined" />
        }
      />

      {/* Warning */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        Caution - These operations modify data across franchise partners.
      </Alert>

      {/* Step 1: Filter */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        1. Filter partners
      </Typography>
      <FilterCard
        filters={filters}
        onFilterChange={updateFilter}
        onClearFilters={clearFilters}
        loading={loading}
      />

      {/* Step 2: Select */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        2. Select partners
      </Typography>

      {/* Results count + pagination */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="textSecondary">
          {filteredData.length} results
          {selectionCount > 0 && ` | ${selectionCount} selected`}
        </Typography>
      </Box>

      <PartnerSelectionTable
        paginatedData={paginatedData}
        filteredData={filteredData}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        isSelected={isSelected}
        isAllSelected={isAllSelected}
        isAllFilteredSelected={isAllFilteredSelected}
        isIndeterminate={isIndeterminate}
        handleSelectFranchise={handleSelectFranchise}
        handleSelectAll={handleSelectAll}
        handleSelectAllFiltered={handleSelectAllFiltered}
        clearSelection={clearSelection}
        disabled={isProcessing}
        loading={loading}
        extraColumns={bulkExtraColumns}
      />

      {/* Step 3: Choose operation */}
      {pageState === 'select' && (
        <>
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            3. Choose operation
          </Typography>

          {/* Selection status banner */}
          <Alert
            severity={selectionCount > 0 ? 'success' : 'error'}
            icon={false}
            sx={{ mb: 2, py: 0.5 }}
          >
            {selectionCount > 0
              ? `${selectionCount} franchise partner${selectionCount > 1 ? 's' : ''} selected`
              : 'Select at least one franchise partner above to continue'}
          </Alert>

          <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              {/* Left: grouped operation list */}
              <Grid item xs={12} md={4}>
                {CATEGORIES.map((cat) => (
                  <Box key={cat} sx={{ mb: 1 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        display: 'block',
                        px: 1,
                        fontSize: 11,
                        letterSpacing: 0.5,
                        fontWeight: 700,
                        color: 'text.primary',
                      }}
                    >
                      {cat}
                    </Typography>
                    <List dense disablePadding>
                      {OPERATIONS.filter((op) => op.category === cat).map((op) => {
                        const isActive = operation === op.key
                        return (
                          <Tooltip
                            key={op.key}
                            title={op.secondary}
                            placement="right"
                            arrow
                            enterDelay={400}
                          >
                            <ListItemButton
                              selected={isActive}
                              onClick={() => {
                                setOperation(op.key)
                                setConfirmed(false)
                              }}
                              sx={{
                                borderLeft: 3,
                                borderLeftColor: isActive ? '#007f98' : 'transparent',
                                py: 0.25,
                                minHeight: 0,
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(0, 127, 152, 0.08)',
                                  '&:hover': { backgroundColor: 'rgba(0, 127, 152, 0.12)' },
                                },
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: isActive ? 600 : 400,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        flex: '1 1 auto',
                                        minWidth: 0,
                                      }}
                                    >
                                      {op.label}
                                    </Typography>
                                    <Chip
                                      label={COST_LABEL[op.costClass]}
                                      size="small"
                                      color={COST_COLOR[op.costClass]}
                                      variant="outlined"
                                      sx={{
                                        height: 16,
                                        fontSize: 9,
                                        fontWeight: 700,
                                        letterSpacing: 0.4,
                                        flexShrink: 0,
                                        '& .MuiChip-label': { px: 0.6 },
                                      }}
                                    />
                                  </Box>
                                }
                                sx={{ my: 0 }}
                              />
                            </ListItemButton>
                          </Tooltip>
                        )
                      })}
                    </List>
                  </Box>
                ))}
              </Grid>

              {/* Right: detail pane */}
              <Grid item xs={12} md={8}>
                <Box sx={{ pl: { md: 3 }, borderLeft: { md: 1 }, borderColor: { md: 'divider' } }}>
                  <Typography variant="h6" gutterBottom>
                    {selectedOp.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {selectedOp.summary}
                  </Typography>

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    What gets updated
                  </Typography>
                  <Box component="ul" sx={{ mt: 0, mb: 3, pl: 2.5 }}>
                    {selectedOp.bullets.map((b, i) => (
                      <Box component="li" key={i} sx={{ mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {b}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                      />
                    }
                    label="I understand this operation modifies data and cannot be undone"
                    sx={{ mb: 2, display: 'block' }}
                  />

                  <Button
                    variant="contained"
                    startIcon={<RunIcon />}
                    onClick={handleRun}
                    disabled={!confirmed || selectionCount === 0}
                    sx={{
                      backgroundColor: '#007f98',
                      '&:hover': { backgroundColor: '#005f73' },
                    }}
                  >
                    Run {operationLabel}
                    {selectionCount > 0 && ` (${selectionCount} FP${selectionCount > 1 ? 's' : ''})`}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
          </Card>
        </>
      )}

      {/* Progress */}
      {pageState === 'processing' && jobStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Running: {operationLabel}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              {jobStatus.progress_message || 'Processing...'}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={jobStatus.progress_percentage}
              sx={{ mb: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="textSecondary">
              {Math.round(jobStatus.progress_percentage)}%
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {pageState === 'complete' && jobStatus?.result_summary && (
        <ResultsCard
          summary={jobStatus.result_summary}
          expandedSection={expandedSection}
          onToggleSection={(section) =>
            setExpandedSection((prev) => (prev === section ? null : section))
          }
          onReset={handleReset}
        />
      )}

      {/* Error */}
      {pageState === 'error' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Operation Failed</AlertTitle>
              {errorMessage || 'An unexpected error occurred'}
            </Alert>
            <Button variant="outlined" onClick={handleReset}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}
    </>
  )
}

export default BulkOperationsPage
